import { supabase } from './supabase.js';

/** Claves de sección (deben coincidir con admin_seguimiento_lecturas.section). */
export const ADMIN_SEG = {
  NOTICIAS_BORRADOR: 'noticias_borrador',
  PUBLICIDAD: 'publicidad',
  PARTNER: 'partner',
  LISTING_REPORT: 'listing_report',
  USER_REPORT: 'user_report',
  MESSAGE_REPORT: 'message_report',
  APP_ERROR: 'app_error',
  REVIEW_LOW: 'review_low',
  COMUNIDAD_PENDIENTE: 'comunidad_pendiente',
  COMUNIDAD_EVENTOS_N8N: 'comunidad_eventos_n8n',
  COMUNIDAD_PORTADA: 'comunidad_portada',
  EVENTO: 'evento',
  EMAIL_FAILED: 'email_failed',
};

/**
 * @param {string} adminId
 * @param {string} section
 * @param {string[]} itemIds
 * @returns {Promise<Set<string>>}
 */
export async function fetchAdminSeguimientoReadIds(adminId, section, itemIds = []) {
  if (!adminId || !section || !itemIds.length) return new Set();
  const { data, error } = await supabase
    .from('admin_seguimiento_lecturas')
    .select('item_id')
    .eq('admin_id', adminId)
    .eq('section', section)
    .in('item_id', itemIds);
  if (error) return new Set();
  return new Set((data ?? []).map(r => r.item_id).filter(Boolean));
}

/**
 * @param {string} adminId
 * @param {string} section
 * @param {string[]} itemIds
 */
export async function countAdminSeguimientoUnread(adminId, section, itemIds) {
  if (!adminId || !section) return 0;
  const ids = (itemIds ?? []).filter(Boolean);
  if (!ids.length) return 0;
  const readIds = await fetchAdminSeguimientoReadIds(adminId, section, ids);
  return ids.filter(id => !readIds.has(id)).length;
}

/**
 * @param {string} adminId
 * @param {string} section
 * @param {() => Promise<{ data?: { id: string }[] | null, error?: unknown }>} loadIds
 */
export async function countAdminSeguimientoUnreadFromQuery(adminId, section, loadIds) {
  if (!adminId || !section) return 0;
  const { data, error } = await loadIds();
  if (error) return 0;
  const ids = (data ?? []).map(r => r.id).filter(Boolean);
  return countAdminSeguimientoUnread(adminId, section, ids);
}

export async function markAdminSeguimientoRead(adminId, section, itemId) {
  if (!adminId || !section || !itemId) return { error: new Error('Faltan datos') };
  return supabase.from('admin_seguimiento_lecturas').upsert(
    {
      admin_id: adminId,
      section,
      item_id: itemId,
      read_at: new Date().toISOString(),
    },
    { onConflict: 'admin_id,section,item_id' }
  );
}

export async function markAdminSeguimientoUnread(adminId, section, itemId) {
  if (!adminId || !section || !itemId) return { error: new Error('Faltan datos') };
  return supabase
    .from('admin_seguimiento_lecturas')
    .delete()
    .eq('admin_id', adminId)
    .eq('section', section)
    .eq('item_id', itemId);
}

export async function markAllAdminSeguimientoRead(adminId, section, itemIds) {
  if (!adminId || !section) return { error: new Error('Faltan datos') };
  const ids = (itemIds ?? []).filter(Boolean);
  if (!ids.length) return { error: null };
  const now = new Date().toISOString();
  return supabase.from('admin_seguimiento_lecturas').upsert(
    ids.map(item_id => ({ admin_id: adminId, section, item_id, read_at: now })),
    { onConflict: 'admin_id,section,item_id' }
  );
}

/** Contadores de badges del menú admin (solo ítems sin leer). */
export async function fetchAdminSeguimientoBadgeCounts(adminUserId) {
  if (!adminUserId) return null;

  const [
    noticias,
    publicidad,
    partnersPending,
    partnersReviewing,
    listingReports,
    userReports,
    messageReports,
    appErrors,
    reviewsLow,
    comunidadPend,
    comunidadEventosN8n,
    comunidadPort,
    eventos,
    emailsFailed,
  ] = await Promise.all([
    countAdminSeguimientoUnreadFromQuery(adminUserId, ADMIN_SEG.NOTICIAS_BORRADOR, () =>
      supabase.from('noticias_musica').select('id').eq('publicado', false)
    ),
    countAdminSeguimientoUnreadFromQuery(adminUserId, ADMIN_SEG.PUBLICIDAD, () =>
      supabase.from('ads').select('id').in('status', ['borrador', 'pendiente_revision'])
    ),
    countAdminSeguimientoUnreadFromQuery(adminUserId, ADMIN_SEG.PARTNER, () =>
      supabase.from('partners').select('id').eq('verification_status', 'pending')
    ),
    countAdminSeguimientoUnreadFromQuery(adminUserId, ADMIN_SEG.PARTNER, () =>
      supabase.from('partners').select('id').eq('verification_status', 'reviewing')
    ),
    countAdminSeguimientoUnreadFromQuery(adminUserId, ADMIN_SEG.LISTING_REPORT, () =>
      supabase.from('listing_reports').select('id').eq('status', 'open')
    ),
    countAdminSeguimientoUnreadFromQuery(adminUserId, ADMIN_SEG.USER_REPORT, () =>
      supabase.from('user_reports').select('id').eq('status', 'open')
    ),
    countAdminSeguimientoUnreadFromQuery(adminUserId, ADMIN_SEG.MESSAGE_REPORT, () =>
      supabase.from('message_reports').select('id').eq('status', 'open')
    ),
    countAdminSeguimientoUnreadFromQuery(adminUserId, ADMIN_SEG.APP_ERROR, () =>
      supabase.from('app_errors').select('id').eq('resuelto', false)
    ),
    countAdminSeguimientoUnreadFromQuery(adminUserId, ADMIN_SEG.REVIEW_LOW, () =>
      supabase.from('reviews').select('id').lte('rating', 2).eq('is_visible', true)
    ),
    countAdminSeguimientoUnreadFromQuery(adminUserId, ADMIN_SEG.COMUNIDAD_PENDIENTE, () =>
      supabase
        .from('comunidad_publicaciones')
        .select('id')
        .eq('aprobada', false)
        .eq('is_hidden', false)
        .or('metadata.is.null,metadata->>source.neq.n8n')
    ),
    countAdminSeguimientoUnreadFromQuery(adminUserId, ADMIN_SEG.COMUNIDAD_EVENTOS_N8N, () =>
      supabase
        .from('comunidad_publicaciones')
        .select('id')
        .eq('aprobada', false)
        .eq('is_hidden', false)
        .eq('metadata->>source', 'n8n')
    ),
    countAdminSeguimientoUnreadFromQuery(adminUserId, ADMIN_SEG.COMUNIDAD_PORTADA, () =>
      supabase
        .from('comunidad_publicaciones')
        .select('id')
        .eq('destacada_solicitud_pendiente', true)
        .eq('destacada', false)
        .eq('aprobada', true)
        .eq('is_hidden', false)
    ),
    countAdminSeguimientoUnreadFromQuery(adminUserId, ADMIN_SEG.EVENTO, () =>
      supabase.from('events').select('id').or('status.eq.pending,status.is.null')
    ),
    countAdminSeguimientoUnreadFromQuery(adminUserId, ADMIN_SEG.EMAIL_FAILED, () =>
      supabase.from('email_outbox').select('id').eq('status', 'failed')
    ),
  ]);

  const reportesUsuario = userReports;
  const reportesListing = listingReports;
  const reportesMensaje = messageReports;
  const reportesError = appErrors;

  return {
    noticias,
    publicidad,
    partners: partnersPending,
    partner_registro: partnersPending + partnersReviewing,
    listings: listingReports,
    valoraciones: reviewsLow,
    reportes_usuario: reportesUsuario,
    reportes_listing: reportesListing,
    reportes_mensaje: reportesMensaje,
    reportes_error: reportesError,
    reportes: reportesUsuario + reportesMensaje + reportesError,
    comunidad_pendientes: comunidadPend,
    comunidad_eventos_n8n: comunidadEventosN8n,
    comunidad_portada: comunidadPort,
    comunidad: comunidadPend + comunidadEventosN8n + comunidadPort,
    eventos,
    emails: emailsFailed,
  };
}
